import { useEffect } from 'react';
import Layout from '../../../components/layout/Layout';
import Breadcrumb from '../../../components/layout/Breadcrumb';
import { yenInc, jbaDispatchExTax } from '../../../utils/pricing';

// 紺帯番号バンド(home/page.tsx NavyBand と同一文法・移植)。帯=区切り(#31)／帯直後に同一文言見出しを反復しない(#33)。
function NavyBand({ no, ja, en }: { no: string; ja: string; en: string }) {
  return (
    <div className="bg-[#1a2942] border-y border-[#a38e65]/25">
      <div className="max-w-[1680px] mx-auto px-4 sm:px-6 lg:px-10 py-5 sm:py-[26px] flex items-center gap-4 sm:gap-6">
        <img src="/images/brand/jba-logo.png" alt="" aria-hidden="true" className="h-8 w-8 sm:h-9 sm:w-9 object-contain shrink-0 opacity-90" />
        <span className="font-['Playfair_Display',serif] text-[#d7b779] text-xl sm:text-[26px] tracking-[0.1em] shrink-0 leading-none">{no}</span>
        <h2 className="jba-display font-medium text-[#f8f6f0] text-lg sm:text-2xl lg:text-[26px] tracking-[0.08em] leading-tight">{ja}</h2>
        <span className="hidden md:block ml-auto font-['Playfair_Display',serif] italic text-[#a38e65] text-base lg:text-[19px] tracking-[0.12em]">{en}</span>
      </div>
    </div>
  );
}

// ===== セカンドスチュワード詳細 全面最新化（Implementer A スコープ） =====
// 土台: about/overview + home。カタログ: Hero=BB-02 / H2-02系 / SEC-N02,N04,N08翻案。
// 事実正典（創作禁止）: 現行 working tree services/second-steward（特徴・シーン・利用パターン・料金）+ hub料金表
//   + mng-thebutler/00_事業説明.md L77(セカンドスチュワード=月額・専任通い)。料金データ変更禁止。

export default function SecondStewardPage() {
  useEffect(() => {}, []);

  const breadcrumbs = [
    { label: 'ホーム', href: '/' },
    { label: '執事サービス', href: '/butler-services/' },
    { label: 'セカンドスチュワード' }
  ];

  const BTN08_ON_LIGHT = "relative inline-flex items-center justify-center bg-[#1a2942] text-[#f8f6f0] border border-[#a38e65] px-[38px] py-[15px] text-sm tracking-[0.2em] font-['Noto_Serif_JP',serif] transition-colors duration-300 hover:bg-[#a38e65] hover:text-[#1a2942] hover:border-[#1a2942] cursor-pointer whitespace-nowrap before:content-[''] before:absolute before:inset-1 before:border before:border-[#a38e65]/45 before:pointer-events-none before:transition-colors before:duration-300 hover:before:border-[#1a2942]/45";
  const BTN08_ON_NAVY = "relative inline-flex items-center justify-center bg-[#a38e65] text-white font-semibold border border-[#1a2942] px-[38px] py-[15px] text-sm tracking-[0.2em] font-['Noto_Serif_JP',serif] transition-colors duration-300 hover:bg-[#1a2942] hover:text-[#f8f6f0] hover:border-[#a38e65] cursor-pointer whitespace-nowrap before:content-[''] before:absolute before:inset-1 before:border before:border-[#1a2942]/40 before:pointer-events-none before:transition-colors before:duration-300 hover:before:border-[#a38e65]/50";

  const features = [
    { en: 'Anticipation', ja: '深い理解に基づくサービス', d: '継続的な関係により、執事はお客様の言葉にならない要望も察知できるようになります。「こうして欲しい」と言う前に、既に準備が整っている。これが真の執事サービスです。' },
    { en: 'Quality of Life', ja: '生活の質の向上', d: '月20日間のサポートにより、生活全体が大きく改善します。時間的余裕が生まれ、ストレスが軽減され、より重要なことに集中できる環境が整います。' },
    { en: 'Trust', ja: '信頼関係の構築', d: '毎月同じ執事が担当することで、お互いの信頼関係が深まります。家族の一員のような安心感の中で、プライベートな領域まで安心してお任せいただけます。' },
    { en: 'Flexible', ja: '柔軟なスケジュール', d: '月20日間を、ご予定に合わせて自由に配分できます。平日のみ、週4日、隔日など、ライフスタイルに最適な利用方法を設計できます。' },
    { en: 'Value', ja: '継続だからの価値', d: `1日あたり${yenInc(jbaDispatchExTax.secondStewardPerDay)}円（${yenInc(jbaDispatchExTax.secondStewardMonthly)}円÷20日）と、単発利用（${yenInc(jbaDispatchExTax.butlerBase)}円/日）と比べて大幅にお得です。質の高いサービスを継続的に、経済的にご利用いただけます。` },
  ];

  const scenes = [
    { t: '多忙な経営者・エグゼクティブ', d: 'ビジネスに専念するため、日常のあらゆる雑務を任せたい経営者の方に。スケジュール管理、来客対応、出張準備、接待サポートなど、ビジネスと生活の両面を支えます。' },
    { t: '高齢のご家族の生活サポート', d: '食事準備、服薬管理、通院付き添い、話し相手など、きめ細やかなケアを継続的に提供。執事が日々の変化に気づき、必要に応じてご家族に報告します。' },
    { t: '産前産後・育児サポート', d: '妊娠中や出産後の大変な時期に、家事全般、上のお子様のケア、栄養バランスを考えた食事準備など、母子の健康を第一に考えたサポートを提供します。' },
    { t: '別荘・プライベートオフィスの管理', d: '定期的に訪れる別荘の使用前準備・滞在中サポート・退去後管理を一貫して担当。自宅をオフィスとする方には、環境維持・来客対応・事務補助を継続的に行います。' },
  ];

  const patterns = [
    { t: '平日のみ', s: '月〜金、週5日×4週=20日', d: '毎日同じ時間帯で、朝の準備から夕食準備まで一貫サポート' },
    { t: '週4日', s: '月・火・木・金×5週=20日', d: '水曜をお休みとし、他の曜日に集中的にサポート' },
    { t: '隔日', s: '一日おきの組み合わせ', d: 'ご予定に完全に合わせた柔軟な利用' },
    { t: '集中利用型', s: '月初と月末に各10日間', d: 'イベント前後や決算期など、特に忙しい時期に対応' },
  ];

  const serviceGroups = [
    { t: '日常生活の完全サポート', d: '家事全般、食事準備、買い物、洗濯、清掃、整理整頓、ペットケア、植物管理' },
    { t: 'パーソナルアシスタント', d: 'スケジュール管理補助、予約代行、贈答品手配、旅行準備、衣替え、季節行事の準備' },
    { t: 'ビジネスサポート', d: '来客対応、会議準備、資料整理、郵便物管理、オフィス環境整備' },
    { t: 'ヘルスケアサポート', d: '通院付き添い、服薬管理、栄養バランスを考慮した食事、健康状態の観察と報告' },
    { t: 'イベント・接待', d: '定期的なホームパーティー準備、接待サポート、季節のイベント企画・実施' },
    { t: '住居管理', d: '定期清掃、設備点検の立ち会い、修理業者の手配、季節に応じた住環境調整' },
  ];

  const faqs = [
    ['継続利用のメリットは何ですか？', '執事がお客様を深く理解することで、言葉にしなくても最適なサービスを提供できるようになります。また、1日あたりの料金が単発利用より大幅にお得です。'],
    ['20日間を自由に配分できますか？', 'はい、完全に自由です。連続20日、週5日×4週、隔日など、ご予定に合わせて設定できます。'],
    ['同じ執事が毎回来ますか？', 'はい、原則として同じ執事が担当します。やむを得ない場合の代理対応も、引き継ぎを徹底します。'],
    ['急な予定変更は可能ですか？', '前日までにご連絡いただければ、日程変更が可能です。月内での振替を原則とします。'],
    ['1ヶ月で試してから継続を決められますか？', 'はい、1ヶ月契約から始められます。ご満足いただければ自動更新となります。ご家族全員の生活を包括的にサポートすることも可能です。'],
  ];

  const related = [
    { name: 'Footman', ja: 'フットマン', cad: '3時間からのスポット利用', price: yenInc(jbaDispatchExTax.footmanBase), unit: '円〜（税込）', href: '/services/footman/' },
    { name: 'Butler', ja: 'バトラー', cad: '1日8時間の専属サービス', price: yenInc(jbaDispatchExTax.butlerBase), unit: '円〜（税込）', href: '/services/butler/' },
    { name: 'House Steward', ja: 'ハウススチュワード', cad: '24時間・3人交代の常駐型', price: yenInc(jbaDispatchExTax.houseStewardMonthly), unit: '円（税込）', href: '/services/house-steward/' },
  ];

  return (
    <Layout>
      <title>セカンドスチュワード｜月20日間の専属執事サービスと料金 | 全日本執事協会 THE BUTLER</title>
      <meta name="description" content={`THE BUTLER のセカンドスチュワードは、同じ専属執事が月20日間・1日8時間通う継続型プラン。深い理解に基づく真のパーソナライズドサービスを、富裕層・ファミリーオフィスへ。料金${yenInc(jbaDispatchExTax.secondStewardMonthly)}円（税込・月額）から、1日あたり${yenInc(jbaDispatchExTax.secondStewardPerDay)}円。`} />
      <link rel="canonical" href="https://butler-association.jp/services/second-steward" />

      <section className="relative bg-[#1a2942] overflow-hidden min-h-[500px] lg:min-h-[580px] flex items-center">
        <img src="/images/generated/jba-p-044-svc-secondsteward.webp" alt="セカンドスチュワードサービス。同じ執事が継続して暮らしを支える様子" className="absolute inset-0 w-full h-full object-cover object-top" />
        <div className="absolute inset-0 bg-[linear-gradient(90deg,rgba(16,20,26,0.92)_0%,rgba(16,20,26,0.72)_48%,rgba(16,20,26,0.35)_100%)]"></div>
        <div className="relative z-10 w-full max-w-[1680px] mx-auto px-4 sm:px-6 lg:px-10 pt-32 pb-16 lg:pt-36 lg:pb-20">
          <div className="max-w-[720px]">
            <span className="block font-['Playfair_Display',serif] uppercase tracking-[0.32em] text-xs sm:text-[13px] text-[#d7b779] mb-4">Plan Ⅲ — Second Steward</span>
            <div className="w-12 h-px bg-[#a38e65] mb-6"></div>
            <h1 className="jba-display font-normal text-white text-[clamp(26px,3.1vw,40px)] leading-[1.4] tracking-[0.04em] mb-6">
              <span className="block">同じ執事が月20日、</span>
              <span className="block">暮らしの基盤を整える</span>
            </h1>
            <p className="font-['Noto_Serif_JP',serif] text-base sm:text-lg text-white/90 leading-[1.95]">
              セカンドスチュワードは、1人の専属執事を1ヶ月20日間継続してご利用いただける、極上のおもてなしプランです。継続的な関係により、執事がお客様の生活リズム・好み・習慣を深く理解し、真の意味でパーソナライズされたサービスを提供します。富裕層、ファミリーオフィス、VVIPのお客様に相応しい、最上級の執事体験をお届けします。
            </p>
          </div>
        </div>
      </section>

      <Breadcrumb items={breadcrumbs} />

      <NavyBand no="I" ja="このサービスについて" en="Overview" />
      <div className="bg-white">
        <div className="max-w-[1320px] mx-auto px-4 sm:px-6 lg:px-10 py-12 sm:py-16">

          {/* S1 特徴 */}
          <section>
            <h2 className="jba-display font-medium text-[#1a2942] text-2xl sm:text-3xl lg:text-[31px] leading-[1.5] tracking-[0.02em] mb-5">
              継続が生む、<span className="text-[#a38e65]">真のパーソナライズ</span>
            </h2>
            <div className="space-y-[18px] font-['Noto_Serif_JP',serif] text-base text-[#333] leading-[1.95] max-w-3xl">
              <p>セカンドスチュワードは、単発のサービスでは決して得られない「継続性」という最大の価値を提供するプランです。1ヶ月間、同じ執事が定期的にお伺いすることで、生活リズム・好み・価値観を深く理解し、まるで長年お仕えしているかのような、細やかで行き届いたサービスを実現します。</p>
              <p>執事という職業の本質は「お客様を知ること」にあります。朝のコーヒーの濃さ、読まれる新聞の種類、好まれる室温、来客時の対応方法など、日々の積み重ねの中で執事が把握する情報は膨大です。この「お客様を知る」プロセスを通じて、単なるサービス提供者ではなく、生活を支える真のパートナーとしての関係を築きます。</p>
              <p>全日本執事協会が認定する上級執事のみが担当するこのサービスは、130年以上の歴史の中で培われた執事文化の真髄を体現しています。技術だけでなく、品格、教養、そして何よりも「仕える心」を持った人材が、お客様の人生に寄り添います。</p>
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-x-10 gap-y-8 mt-10">
              {features.map((f) => (
                <div key={f.ja} className="border-t border-[#e2ddd2] pt-6">
                  <span className="block font-['Playfair_Display',serif] italic text-[#8f7a56] text-[13px] tracking-[0.06em] mb-1">{f.en}</span>
                  <h3 className="jba-display font-medium text-[#1a2942] text-lg sm:text-xl mb-2.5">{f.ja}</h3>
                  <p className="font-['Noto_Serif_JP',serif] text-sm text-[#4a4638] leading-[1.9]">{f.d}</p>
                </div>
              ))}
            </div>
          </section>
        </div>
      </div>

      <NavyBand no="II" ja="ご利用シーン" en="Scenes" />
      <div className="bg-white">
        <div className="max-w-[1320px] mx-auto px-4 sm:px-6 lg:px-10 py-12 sm:py-16">

          {/* S2 シーン（画像split） */}
          <section>
            <h2 className="jba-display font-medium text-[#1a2942] text-2xl sm:text-3xl lg:text-[31px] leading-[1.5] tracking-[0.02em] mb-6">
              最も価値を発揮するライフスタイル
            </h2>
            <div className="grid lg:grid-cols-[340px_1fr] gap-8 lg:gap-12 items-start">
              <img src="/images/generated/jba-fs-242-services_second-steward.webp" alt="セカンドスチュワードによる日常的なサポート" className="w-full h-64 lg:h-[360px] object-cover border border-[#a38e65]/30" />
              <div className="grid sm:grid-cols-2 gap-x-8 gap-y-7">
                {scenes.map((s) => (
                  <div key={s.t} className="border-l-2 border-[#a38e65]/40 pl-4">
                    <h3 className="jba-display font-medium text-[#1a2942] text-lg mb-2">{s.t}</h3>
                    <p className="font-['Noto_Serif_JP',serif] text-sm text-[#4a4638] leading-[1.9]">{s.d}</p>
                  </div>
                ))}
              </div>
            </div>
          </section>
        </div>
      </div>

      <NavyBand no="III" ja="ご利用パターン" en="Usage Patterns" />
      <div className="bg-white">
        <div className="max-w-[1320px] mx-auto px-4 sm:px-6 lg:px-10 py-12 sm:py-16">

          {/* S3 利用パターン（4カード） */}
          <section>
            <h2 className="jba-display font-medium text-[#1a2942] text-2xl sm:text-3xl lg:text-[31px] leading-[1.5] tracking-[0.02em] mb-3">
              月20日の使い方
            </h2>
            <p className="font-['Noto_Serif_JP',serif] text-base text-[#333] leading-[1.95] max-w-3xl mb-9">
              月20日間は、ご予定に合わせて自由に配分できます。以下は代表的なパターンです。ライフスタイルに合わせて最適なスケジュールを設計します。
            </p>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {patterns.map((p) => (
                <div key={p.t} className="bg-[#f7f5f0] p-6">
                  <h3 className="jba-display font-medium text-[#1a2942] text-lg mb-2">{p.t}</h3>
                  <p className="font-['Playfair_Display',serif] text-sm text-[#a38e65] mb-3">{p.s}</p>
                  <p className="font-['Noto_Serif_JP',serif] text-sm text-[#4a4638] leading-[1.85]">{p.d}</p>
                </div>
              ))}
            </div>
          </section>
        </div>
      </div>

      <NavyBand no="IV" ja="提供サービス" en="What We Do" />
      <div className="bg-white">
        <div className="max-w-[1320px] mx-auto px-4 sm:px-6 lg:px-10 py-12 sm:py-16">

          {/* S4 提供サービス内容（画像split） */}
          <section>
            <h2 className="jba-display font-medium text-[#1a2942] text-2xl sm:text-3xl lg:text-[31px] leading-[1.5] tracking-[0.02em] mb-6">
              継続だからできる包括的サポート
            </h2>
            <div className="grid lg:grid-cols-[1fr_320px] gap-8 lg:gap-12 items-start">
              <div className="grid md:grid-cols-2 gap-x-10 gap-y-8">
                {serviceGroups.map((g) => (
                  <div key={g.t} className="border-t border-[#e2ddd2] pt-6">
                    <h3 className="jba-display font-medium text-[#1a2942] text-lg mb-2.5">{g.t}</h3>
                    <p className="font-['Noto_Serif_JP',serif] text-sm text-[#4a4638] leading-[1.9]">{g.d}</p>
                  </div>
                ))}
              </div>
              <img src="/images/generated/jba-fs-243-services_second-steward.webp" alt="セカンドスチュワードによる生活全般のサポート" className="w-full h-64 lg:h-[420px] object-cover border border-[#a38e65]/30 order-first lg:order-none" />
            </div>
          </section>
        </div>
      </div>

      <NavyBand no="V" ja="料金" en="Pricing" />
      <div className="bg-white">
        <div className="max-w-[1320px] mx-auto px-4 sm:px-6 lg:px-10 py-12 sm:py-16">

          {/* S5 料金 */}
          <section>
            <h2 className="jba-display font-medium text-[#1a2942] text-2xl sm:text-3xl lg:text-[31px] leading-[1.5] tracking-[0.02em] mb-6">
              明確でわかりやすい料金
            </h2>
            <div className="grid lg:grid-cols-[300px_1fr] gap-8 lg:gap-12 items-start">
              <div className="bg-[#1a2942] p-8 text-center">
                <div className="font-['Noto_Serif_JP',serif] text-[38px] text-[#f8f6f0] leading-none">{yenInc(jbaDispatchExTax.secondStewardMonthly)}<span className="text-base text-[#d7b779] ml-1">円</span></div>
                <p className="font-['Noto_Serif_JP',serif] text-sm text-[#ded8cc] mt-3">税込・月額</p>
                <p className="font-['Noto_Serif_JP',serif] text-[15px] text-[#d7b779] mt-4">1日あたり {yenInc(jbaDispatchExTax.secondStewardPerDay)}円<span className="text-xs text-[#ded8cc] ml-1">（20日利用時）</span></p>
              </div>
              <div>
                <dl className="border-t-2 border-[#1a2942]">
                  {[
                    ['月額基本料金', `${yenInc(jbaDispatchExTax.secondStewardMonthly)}円〜（税込）／1日8時間×20日間`],
                    ['利用時間帯', '8:00〜21:00の間で8時間'],
                    ['時間外・延長', `${yenInc(jbaDispatchExTax.secondStewardExtension)}円/時間（税込）`],
                    ['追加日数', `${yenInc(jbaDispatchExTax.secondStewardAddDay)}円/日（税込）`],
                    ['執事指名', '無料'],
                    ['交通費', '月額定額制または実費精算'],
                  ].map(([k, v]) => (
                    <div key={k} className="flex justify-between gap-6 border-b border-[#e2ddd2] py-4">
                      <dt className="font-['Noto_Serif_JP',serif] text-sm text-[#8f7a56] tracking-[0.06em] shrink-0">{k}</dt>
                      <dd className="font-['Noto_Serif_JP',serif] text-[16px] text-[#1a2942] text-right">{v}</dd>
                    </div>
                  ))}
                </dl>
                <p className="font-['Noto_Serif_JP',serif] text-sm text-[#4a4638] leading-[1.85] mt-6">
                  <span className="text-[#8f7a56]">お支払い：</span>銀行振込（月払い）、クレジットカード。3ヶ月一括払いで3%割引、6ヶ月一括払いで5%割引。契約は1ヶ月単位の自動更新です。
                </p>
              </div>
            </div>
          </section>
        </div>
      </div>

      <NavyBand no="VI" ja="よくあるご質問" en="FAQ" />
      <div className="bg-white">
        <div className="max-w-[1320px] mx-auto px-4 sm:px-6 lg:px-10 py-12 sm:py-16">

          {/* S6 FAQ */}
          <section>
            <h2 className="jba-display font-medium text-[#1a2942] text-2xl sm:text-3xl lg:text-[31px] leading-[1.5] tracking-[0.02em] mb-6">
              よくあるご質問
            </h2>
            <div className="max-w-3xl divide-y divide-[#e2ddd2] border-t border-[#e2ddd2]">
              {faqs.map(([q, a]) => (
                <div key={q} className="py-6">
                  <h3 className="jba-display font-medium text-[#1a2942] text-lg mb-2.5">Q. {q}</h3>
                  <p className="font-['Noto_Serif_JP',serif] text-[15px] text-[#4a4638] leading-[1.9]">{a}</p>
                </div>
              ))}
            </div>
          </section>
        </div>
      </div>

      <NavyBand no="VII" ja="その他のプラン" en="Other Plans" />
      <div className="bg-white">
        <div className="max-w-[1320px] mx-auto px-4 sm:px-6 lg:px-10 py-12 sm:py-16">

          {/* S7 関連サービス */}
          <section>
            <h2 className="jba-display font-medium text-[#1a2942] text-2xl sm:text-3xl lg:text-[31px] leading-[1.5] tracking-[0.02em] mb-3">
              他のプランと比べる
            </h2>
            <p className="font-['Noto_Serif_JP',serif] text-base text-[#333] leading-[1.95] max-w-3xl mb-9">
              まずお試しならフットマン、特別な一日にはバトラー、邸宅全体を24時間任せるならハウススチュワードへ。
            </p>
            <div className="grid sm:grid-cols-3 gap-6">
              {related.map((r) => (
                <a key={r.name} href={r.href} className="group block border border-[#e2ddd2] hover:border-[#a38e65] transition-colors duration-300 p-6">
                  <span className="block font-['Playfair_Display',serif] text-[13px] tracking-[0.1em] text-[#8f7a56] mb-1">{r.name}</span>
                  <h3 className="jba-display font-medium text-[#1a2942] text-lg mb-2 group-hover:text-[#a38e65] transition-colors">{r.ja}</h3>
                  <p className="font-['Noto_Serif_JP',serif] text-sm text-[#4a4638] mb-4">{r.cad}</p>
                  <div className="font-['Noto_Serif_JP',serif] text-[22px] text-[#1a2942]">{r.price}<span className="text-sm text-[#8f7a56] ml-1">{r.unit}</span></div>
                </a>
              ))}
            </div>
            <div className="mt-8"><a href="/butler-services/" className={BTN08_ON_LIGHT}>執事サービス一覧へ戻る</a></div>
          </section>
        </div>
      </div>

      <div className="bg-white">
        <div className="max-w-[1320px] mx-auto px-4 sm:px-6 lg:px-10 py-12 sm:py-16">

          {/* S8 CTA */}
          <section>
            <div className="bg-[#1a2942] border border-[#a38e65]/45 p-8 sm:p-10 lg:p-12">
              <h2 className="jba-display font-medium text-[#f8f6f0] text-xl sm:text-2xl lg:text-[28px] leading-[1.5] mb-4">セカンドスチュワードで上質な生活を</h2>
              <p className="font-['Noto_Serif_JP',serif] text-base text-[#e7e2d8] leading-[1.95] max-w-3xl mb-8">
                専属執事が、あなたの生活を変えます。まずはお気軽にご相談ください。ライフスタイルを丁寧にお伺いし、最適なプランとスケジュールをご提案します。
              </p>
              <div className="flex flex-col sm:flex-row sm:items-center gap-5">
                <a href="/contact" className={BTN08_ON_NAVY}>お問い合わせフォームへ</a>
                <span className="font-['Noto_Serif_JP',serif] text-[#e7e2d8] text-[15px]">お電話：03-6824-4086（平日10:00-18:00）</span>
              </div>
            </div>
          </section>

        </div>
      </div>
    </Layout>
  );
}
